import { ref, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import axios from '@/lib/API/client'
import fetcher from '@/lib/API/fetcher'
import useSWRV, { mutate } from 'swrv'
import { format } from '@/lib/helpers/time'
import tailwind from '@/lib/tailwind'
import { healthScoreFromPresentation } from '@/lib/health-presentation'
import { primaryHorseFirst } from '@/lib/horses/primary'
import { activeHorsesOf } from '@/lib/horses/archived'
import { dayKey } from '@/lib/programs/program-days'

export default () => {
    const route = useRoute()
    const store = useStore()

    const currentDate = ref(new Date())
    const horseId = ref('')
    const year = ref(currentDate.value.getFullYear())
    const month = ref(currentDate.value.getMonth() + 1) //month start a 0
    const byInterval = ref('daily') //month start a 0

    /** BEGIN HORSETRACK SECTION */
    const healthZones = [
        { start: 0, end: 25, color: tailwind.theme.color.state.red }, // Danger zone
        { start: 25, end: 60, color: tailwind.theme.color.state.orange }, // Warning zone
        { start: 60, end: 100, color: tailwind.theme.color.state.green }, // Healthy zone
    ]

    // Utility function to determine health status and color based on health zones
    function getHealthStatus(healthLevel, healthZones) {
        const zone = healthZones.find((z) => healthLevel <= z.end) // Find the matching health zone
        if (healthLevel === null || !zone) return { status: null, color: null }
        const status = zone === healthZones[0] ? 'danger' : zone === healthZones[1] ? 'warning' : 'good'
        return { status, color: zone.color } // Return the status and the color of the matched zone
    }

    function healthLevelFromReport(report) {
        return healthScoreFromPresentation(report)
    }

    function healthPresentationFromReport(report, healthLevel) {
        const fallback = getHealthStatus(healthLevel, healthZones)

        return {
            status: report?.health_status || fallback.status,
            color: report?.health_color || report?.health?.color || fallback.color,
        }
    }

    function sortByStartAt(reports) {
        return [...reports].sort((a, b) => new Date(b.start_at) - new Date(a.start_at))
    }

    function localDayKey(dateValue) {
        const date = new Date(dateValue)
        if (!Number.isFinite(date.getTime())) return null

        return dayKey(date)
    }

    const enrichedHorsetrack = computed(() => {
        const reports = horsetrack.value || []
        const sortedReports = sortByStartAt(reports)

        return sortedReports.map((report, index, array) => {
            const healthLevel = healthLevelFromReport(report)
            const { status: healthStatus, color } = healthPresentationFromReport(report, healthLevel)

            let trend = null

            if (index < array.length - 1) {
                const next = array[index + 1]
                const nextHealthLevel = healthLevelFromReport(next)

                if (healthLevel !== null && nextHealthLevel !== null) {
                    trend = healthLevel - nextHealthLevel
                }
            }

            return {
                ...report,
                healthLevel,
                healthStatus,
                color,
                trend,
            }
        })
    })

    function calculateHealthReportsAndAverage(from, to) {
        const reports = enrichedHorsetrack.value || []

        const filteredReports = reports.filter((report) => {
            if (!report.start_at) return false
            const reportDate = new Date(report.start_at).getTime()
            return reportDate >= from.getTime() && reportDate < to.getTime()
        })

        const scoredReports = filteredReports.filter((report) => Number.isFinite(report.healthLevel))
        const healthLevelsByDay = scoredReports.reduce((days, report) => {
            const dayKey = localDayKey(report.start_at)
            if (!dayKey) return days

            const values = days.get(dayKey) || []
            values.push(report.healthLevel)
            days.set(dayKey, values)

            return days
        }, new Map())

        const dailyHealthLevels = Array.from(healthLevelsByDay.values()).map((healthLevels) => {
            const totalHealth = healthLevels.reduce((sum, healthLevel) => sum + healthLevel, 0)
            return totalHealth / healthLevels.length
        })

        const totalHealth = dailyHealthLevels.reduce((sum, healthLevel) => sum + healthLevel, 0)
        const healthLevel = dailyHealthLevels.length > 0 ? Math.round(totalHealth / dailyHealthLevels.length) : null
        const { status: healthStatus, color } = getHealthStatus(healthLevel, healthZones)

        const average = {
            healthLevel,
            healthStatus,
            color,
            totalReports: scoredReports.length,
        }

        return { reports: scoredReports, average }
    }
    /** END HORSETRACK SECTION */

    const { data: loadedHorses, error } = useSWRV('/user/horses?all=1', fetcher, {})
    const horses = computed(() => primaryHorseFirst(loadedHorses.value, store.state.auth?.user?.primary_horse_id))
    // An archived horse is hidden everywhere but the horses list: `horses` (archived ones
    // included) is only for that list and lookups by id; every picker, filter and count
    // uses `activeHorses`.
    const activeHorses = computed(() => activeHorsesOf(horses.value))

    const { data: horse, isValidating: isValidatingHorse } = useSWRV(
        () => horseId.value && `/horses/${horseId.value}`,
        () => fetchHorse(horseId.value),
        { revalidateOnFocus: false }
    )

    const { data: horseStatistics } = useSWRV(
        () =>
            horseId.value &&
            `/horses/${horseId.value}/reporting?month=${month.value}&year=${year.value}&by=${byInterval.value}`,
        fetcher,
        {}
    )

    const { data: horsetrack, isValidating: isValidatingHorsetrack } = useSWRV(
        () => horseId.value && `horses/${horseId.value}/horsetrack?at=${currentDate.value?.toISOString()}`,
        fetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 5000,
        }
    )

    /**
     *
     * mutate* methods
     *
     **/

    const mutateHorses = (data) => {
        mutate('/user/horses?all=1', data ? data : fetcher('/user/horses?all=1', undefined, { forceNetwork: true }))
    }

    const mutateHorse = (horseId, data) => {
        mutate(`/horses/${horseId}`, data ? setHorse(data) : fetchHorse(horseId, { forceNetwork: true }))
    }

    /**
     *
     * fetch* methods
     *
     **/
    const fetchHorse = (horseId, options = {}) => {
        return fetcher(`/horses/${horseId}`, setHorse, options)
    }

    /**
     *
     * internal methods
     *
     **/
    const setHorse = (newHorse = {}) => {
        Object.keys(newHorse).forEach((key) => {
            switch (key) {
                case 'born_at':
                    newHorse[key] = newHorse[key] ? format(newHorse[key].replace('Z', ''), 'YYYY-MM-DD') : ''
                    break
                default:
                    newHorse[key] = newHorse[key] ?? ''
            }
        })
        return newHorse
    }

    /**
     *
     * API methods
     *
     **/
    const updateHorse = (horseId, data) => {
        return axios.put(`/horses/${horseId}`, data).then((response) => {
            mutateHorse(horseId, response.data)
            mutateHorses()
            return response.data
        })
    }

    const removeHorse = (horseId) => {
        return axios.delete(`/horses/${horseId}`).then((response) => {
            mutateHorse(horseId, {})
            mutateHorses()
            return response.data
        })
    }

    // Archived horses stay readable with their history; they only leave the
    // default roster. Archiving is shared with everyone who sees the horse.
    const setHorseArchived = (horseId, archived) => {
        const request = archived ? axios.put : axios.delete
        return request(`/horses/${horseId}/archive`).then((response) => {
            mutateHorse(horseId)
            mutateHorses()
            return response.data
        })
    }

    const updateHorseAvatar = (horseId, file) => {
        let formData = new FormData()
        formData.append('avatar', file)
        return axios
            .post(`/horses/${horseId}/avatar`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then((response) => {
                mutateHorse(horseId, response.data)
                mutateHorses()
                return response.data
            })
    }

    const createHorse = (data) => {
        return axios.post('/horses', data).then((response) => {
            mutateHorse(response.data.id, response.data)
            mutateHorses()
            return response.data
        })
    }

    const shareHorse = (horseId, email, job = null) => {
        return axios.post(`/horses/${horseId}/sharing/invite`, { email, job }).then((response) => {
            return response.data
        })
    }

    const unshareHorse = (horseId, userId) => {
        return axios.delete(`/horses/${horseId}/sharing/${userId}`).then((response) => {
            console.log('response', response)
            // mutateHorse(response.data.id, response.data)
            mutateHorses()
            return response.data
        })
    }

    const transferOwnershipHorse = (horseId, newOwnerId) => {
        return axios.put(`/horses/${horseId}/owners`, { newOwnerId }).then((response) => {
            mutateHorses()
            return response.data
        })
    }

    watch(
        [() => route.params.horse],
        async ([newHorseId], [oldHorseId]) => {
            horseId.value = newHorseId ?? oldHorseId ?? ''
        },
        { immediate: true }
    )

    watch(
        currentDate,
        async () => {
            year.value = currentDate.value.getFullYear()
            month.value = currentDate.value.getMonth() + 1 //month start a 0
        },
        { immediate: true }
    )

    return {
        //data
        healthZones,
        horses,
        activeHorses,
        error,
        horse,
        isValidatingHorse,
        horsetrack,
        isValidatingHorsetrack,
        horseStatistics,
        calculateHealthReportsAndAverage,
        horseId,
        year,
        month,
        currentDate,
        byInterval,
        //methods
        getHealthStatus,
        setHorse,
        updateHorse,
        removeHorse,
        setHorseArchived,
        updateHorseAvatar,
        createHorse,
        shareHorse,
        unshareHorse,
        mutateHorse,
        transferOwnershipHorse,
    }
}
